import { CommonActivityType } from './activity-history/common-activity-type.enum';

/**
 * Activity types for Sourcing Request activity history.
 * Extends CommonActivityType with Sourcing Request-specific types.
 */
export const SourcingRequestActivityType = {
	...CommonActivityType,
	// Sourcing Request-specific types
	LINE_ADDED: 'line_added',
	LINE_UPDATED: 'line_updated',
	LINE_REMOVED: 'line_removed',
	LINE_STATUS_CHANGED: 'line_status_changed',
	VENDOR_ASSIGNED: 'vendor_assigned',
	ITEM_MATCHED: 'item_matched',
	PO_CREATED: 'po_created',
	RFQ_SENT: 'rfq_sent',
	RFQ_RESPONSE_RECEIVED: 'rfq_response_received',
	RFQ_AWARDED: 'rfq_awarded',
	RFQ_CANCELLED: 'rfq_cancelled',
	RFQ_SENT_TO_AD_HOC_CONTACT: 'rfq_sent_to_ad_hoc_contact',
	ISSUE_LINKED: 'issue_linked',
	ISSUE_UNLINKED: 'issue_unlinked',
	ASSIGNED: 'assigned',
	REOPENED: 'reopened',
} as const;

export type SourcingRequestActivityType =
	(typeof SourcingRequestActivityType)[keyof typeof SourcingRequestActivityType];

/**
 * Labels for activity types (for display in filter bar)
 */
export const SOURCING_REQUEST_ACTIVITY_TYPE_LABELS: Record<
	SourcingRequestActivityType,
	string
> = {
	// Common types
	[SourcingRequestActivityType.CREATED]: 'Created',
	[SourcingRequestActivityType.UPDATED]: 'Updated',
	[SourcingRequestActivityType.DELETED]: 'Deleted',
	[SourcingRequestActivityType.RECOVERED]: 'Recovered',
	[SourcingRequestActivityType.STATUS_CHANGED]: 'Status Changed',
	[SourcingRequestActivityType.PRIORITY_CHANGED]: 'Priority Changed',
	[SourcingRequestActivityType.ASSIGNEE_CHANGED]: 'Assignee Changed',
	[SourcingRequestActivityType.NOTE_ADDED]: 'Note Added',
	[SourcingRequestActivityType.NOTE_UPDATED]: 'Note Updated',
	[SourcingRequestActivityType.NOTE_DELETED]: 'Note Deleted',
	[SourcingRequestActivityType.TASK_LINKED]: 'Task Linked',
	[SourcingRequestActivityType.TASK_UNLINKED]: 'Task Unlinked',
	[SourcingRequestActivityType.TASK_CREATED]: 'Task Created',
	[SourcingRequestActivityType.TASK_COMPLETED]: 'Task Completed',
	[SourcingRequestActivityType.TASK_UPDATED]: 'Task Updated',
	[SourcingRequestActivityType.TASK_DELETED]: 'Task Deleted',
	[SourcingRequestActivityType.FILE_ATTACHED]: 'File Attached',
	[SourcingRequestActivityType.FILE_REMOVED]: 'File Removed',
	[SourcingRequestActivityType.ENTITY_LINKED]: 'Entity Linked',
	[SourcingRequestActivityType.ENTITY_UNLINKED]: 'Entity Unlinked',
	[SourcingRequestActivityType.WATCHER_ADDED]: 'Watcher Added',
	[SourcingRequestActivityType.WATCHER_REMOVED]: 'Watcher Removed',
	[SourcingRequestActivityType.AI_SESSION]: 'AI Session',
	// SR-specific types
	[SourcingRequestActivityType.LINE_ADDED]: 'Line Added',
	[SourcingRequestActivityType.LINE_UPDATED]: 'Line Updated',
	[SourcingRequestActivityType.LINE_REMOVED]: 'Line Removed',
	[SourcingRequestActivityType.LINE_STATUS_CHANGED]: 'Line Status Changed',
	[SourcingRequestActivityType.VENDOR_ASSIGNED]: 'Vendor Assigned',
	[SourcingRequestActivityType.ITEM_MATCHED]: 'Item Matched',
	[SourcingRequestActivityType.PO_CREATED]: 'PO Created',
	[SourcingRequestActivityType.RFQ_SENT]: 'RFQ Sent',
	[SourcingRequestActivityType.RFQ_RESPONSE_RECEIVED]: 'RFQ Response Received',
	[SourcingRequestActivityType.RFQ_AWARDED]: 'RFQ Awarded',
	[SourcingRequestActivityType.RFQ_CANCELLED]: 'RFQ Cancelled',
	[SourcingRequestActivityType.RFQ_SENT_TO_AD_HOC_CONTACT]:
		'RFQ Sent to Ad-Hoc Contact',
	[SourcingRequestActivityType.ISSUE_LINKED]: 'Issue Linked',
	[SourcingRequestActivityType.ISSUE_UNLINKED]: 'Issue Unlinked',
	[SourcingRequestActivityType.ASSIGNED]: 'Assigned',
	[SourcingRequestActivityType.REOPENED]: 'Reopened',
};

/**
 * Group activity types for filter bar categories
 */
export const SOURCING_REQUEST_ACTIVITY_GROUPS = {
	all: [] as Array<SourcingRequestActivityType>,
	status: [
		SourcingRequestActivityType.CREATED,
		SourcingRequestActivityType.UPDATED,
		SourcingRequestActivityType.STATUS_CHANGED,
		SourcingRequestActivityType.PRIORITY_CHANGED,
		SourcingRequestActivityType.ASSIGNED,
		SourcingRequestActivityType.ASSIGNEE_CHANGED,
		SourcingRequestActivityType.REOPENED,
	],
	lines: [
		SourcingRequestActivityType.LINE_ADDED,
		SourcingRequestActivityType.LINE_UPDATED,
		SourcingRequestActivityType.LINE_REMOVED,
		SourcingRequestActivityType.LINE_STATUS_CHANGED,
	],
	vendors: [
		SourcingRequestActivityType.VENDOR_ASSIGNED,
		SourcingRequestActivityType.ITEM_MATCHED,
		SourcingRequestActivityType.PO_CREATED,
	],
	rfq: [
		SourcingRequestActivityType.RFQ_SENT,
		SourcingRequestActivityType.RFQ_RESPONSE_RECEIVED,
		SourcingRequestActivityType.RFQ_AWARDED,
		SourcingRequestActivityType.RFQ_CANCELLED,
		SourcingRequestActivityType.RFQ_SENT_TO_AD_HOC_CONTACT,
	],
	notes: [
		SourcingRequestActivityType.NOTE_ADDED,
		SourcingRequestActivityType.NOTE_UPDATED,
		SourcingRequestActivityType.NOTE_DELETED,
	],
	tasks: [
		SourcingRequestActivityType.TASK_LINKED,
		SourcingRequestActivityType.TASK_UNLINKED,
		SourcingRequestActivityType.TASK_CREATED,
		SourcingRequestActivityType.TASK_COMPLETED,
	],
	issues: [
		SourcingRequestActivityType.ISSUE_LINKED,
		SourcingRequestActivityType.ISSUE_UNLINKED,
	],
	files: [
		SourcingRequestActivityType.FILE_ATTACHED,
		SourcingRequestActivityType.FILE_REMOVED,
	],
	watchers: [
		SourcingRequestActivityType.WATCHER_ADDED,
		SourcingRequestActivityType.WATCHER_REMOVED,
	],
};
